import React, { useEffect, useCallback, useState } from "react";
import {
  Modal,
  Button,
  ButtonGroup,
  Select,
} from "@veneer/core";
import { createTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

import useWeeklyAgenda, { WeekDayKey } from "../../../hooks/weeklyAgenda/useWeeklyAgenda";
import { WeeklyAgendaCreationBlock } from "./WeeklyAgendaCreationBlock";
import AgendaManagement from "../../../services/agendaApi/agendaManagement";
import { AppointmentTypeEnum } from "../../../models/enum/AgendaApi/AppointmentTypeEnum";
import { ModalMessageAgenda } from "./ModalMessageAgenda";
import { ICreateWeeklyAppointmentResultDto } from "../../../models/input/agendaApi/ICreateAppoiment";

const muiTheme = createTheme({
  palette: { primary: { main: "#027aae" } },
});

const useDayLabels = (): Record<WeekDayKey, string> => {
  const [t] = useTranslation("global");
  // No useMemo — t reference may not change identity on i18n.changeLanguage(), causing stale labels
  return {
    MONDAY:    t("header.monday"),
    TUESDAY:   t("header.tuesday"),
    WEDNESDAY: t("header.wednesday"),
    THURSDAY:  t("header.thursday"),
    FRIDAY:    t("header.friday"),
    SATURDAY:  t("header.saturday"),
    SUNDAY:    t("header.sunday"),
  };
};

interface WeeklyAgendaModalProps {
  show: boolean;
  onClose: () => void;
  userID: number;
  onCreated?: () => void;
  onPartialCreated?: () => void;
  onError?: () => void;
  onAllFailed?: () => void;
}

const WeeklyAgendaModal: React.FC<WeeklyAgendaModalProps> = ({
  show,
  onClose,
  userID,
  onCreated,
  onPartialCreated,
  onError,
  onAllFailed,
}) => {
  const [t] = useTranslation("global");
  const dayLabels = useDayLabels();

  const [conflictModalOpen, setConflictModalOpen] = useState(false);
  const [conflictMessage, setConflictMessage] = useState("");

  const {
    typeOfVisitId,
    subRetail,
    pointOfSale,
    startDate,
    endDate,
    daySchedules,

    subRetailData,
    setSubRetailData,
    subRetailDataCopy,
    pointOfSaleData,
    setPointOfSaleData,
    pointOfSaleDataCopy,

    disableSubretail,
    disablePointOfSale,
    errorPos,
    errorDatePicker,

    visibleWeekdays,
    isSubmitDisabled,

    handleSubRetailChange,
    handleSubRetailChangeList,
    handleSubRetailClear,
    handlePointOfSaleChange,
    handlePointOfSaleChangeList,
    handlePointOfSaleClear,
    handleChangeDateRange,
    handleDayToggle,
    handleDayStartTime,
    handleDayEndTime,

    loadSubRetail,
    reset,
    buildCreateWeeklyAppointmentPayload,
  } = useWeeklyAgenda();

  useEffect(() => {
    if (show) {
      loadSubRetail();
    }
  }, [show, loadSubRetail]);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const handleSubmit = useCallback(() => {
    if (userID <= 0) return;
    const payload = buildCreateWeeklyAppointmentPayload(userID);
    AgendaManagement.createWeeklyAppointment(payload)
      .then((response) => {
        const results = Array.isArray(response)
          ? (response as ICreateWeeklyAppointmentResultDto[])
          : [];

        const failed = results.filter((r) => r.result === 0);
        const allFailed = failed.length > 0 && failed.length === results.length;
        const partialFailed = failed.length > 0 && !allFailed;

        if (allFailed) {
          onAllFailed?.();
        } else if (partialFailed) {
          setConflictMessage(t("header.weeklyAgendaConflictMessage"));
          setConflictModalOpen(true);
          onPartialCreated?.();
        } else {
          onCreated?.();
        }
        handleClose();
      })
      .catch(() => {
        onError?.();
        handleClose();
      });
  }, [userID, buildCreateWeeklyAppointmentPayload, onCreated, onPartialCreated, onError, onAllFailed, handleClose, t]);

  return (
    <div style={{ position: "relative" }}>
      <ModalMessageAgenda
        openModal={conflictModalOpen}
        closeModal={setConflictModalOpen}
        title="Message"
        message={conflictMessage}
      />
      <Modal
        className="modalAddAgenda"
        closeButton={true}
        align="center"
        id="modalWeeklyAgenda"
        show={show}
        onClose={handleClose}
        footer={
          <ButtonGroup className="createAgendaButtongrouop">
            <Button disabled={isSubmitDisabled} onClick={handleSubmit}>
              {t("header.submit")}
            </Button>
            <Button onClick={handleClose} appearance="secondary">
              {t("header.cancel")}
            </Button>
          </ButtonGroup>
        }
        title={t("header.weeklyAgenda")}
      >
        <div className="createAgenda">
          <Select
            className="createAgendaSelect"
            label={t("header.typeOfVisit")}
            value={[AppointmentTypeEnum.Visit]}
            options={[{ value: AppointmentTypeEnum.Visit, label: t("header.visit") }]}
            disabled={true}
          />
          <WeeklyAgendaCreationBlock
            typeOfVisitID={typeOfVisitId}
            labelSubretail={t("header.subRetail")}
            handleChangeSubRetail={handleSubRetailChange}
            handleChangeSubRetailList={handleSubRetailChangeList}
            subretailData={subRetailData}
            subRetail={subRetail}
            onClearSubRetail={handleSubRetailClear}
            disableSubretail={disableSubretail}
            subretailDataCopy={subRetailDataCopy}
            setSubretailData={setSubRetailData}
            labelPointOfSale={t("header.pointOfSale")}
            handleChangePointOfSale={handlePointOfSaleChange}
            handleChangePointOfSaleList={handlePointOfSaleChangeList}
            pointOfSaleData={pointOfSaleData}
            pointOfSale={pointOfSale}
            setPointOfSale={handlePointOfSaleClear}
            disablePointOfSale={disablePointOfSale}
            errorPos={errorPos}
            pointOfSaleDataCopy={pointOfSaleDataCopy}
            setPointOfSaleData={setPointOfSaleData}
            labelStardDate={t("header.stardDate")}
            labelEndDate={t("header.endDate")}
            startDate={startDate}
            endDate={endDate}
            handleChangeStartDate={handleChangeDateRange}
            errorDatePicker={errorDatePicker}
            theme={muiTheme}
            labelStartTime={t("header.startTime")}
            labelEndTime={t("header.endTime")}
            visibleWeekdays={visibleWeekdays}
            daySchedules={daySchedules}
            dayLabels={dayLabels}
            onDayToggle={handleDayToggle}
            onDayStartTime={handleDayStartTime}
            onDayEndTime={handleDayEndTime}
          />
        </div>
      </Modal>
    </div>
  );
};

export default WeeklyAgendaModal;
